'use strict';

import { getLoggedServingsOfRecipe } from './Meals';
import { roundToDecimals } from './Math';

/**
 * Creates a scaling map from dishes array
 * Maps dish UUID/ID to its scaling value
 *
 * @param {Array} dishes - Array of dish objects with main property
 * @returns {Object} Map of dish UUID/ID to scaling value
 *
 * @example
 * const dishes = [
 *   { main: { uuid: 'abc123', scaling: 2 } },
 *   { main: { id: 'def456', scaling: 3 } }
 * ];
 * createScalingMap(dishes); // { 'abc123': 2, 'def456': 3 }
 */
export function createScalingMap(dishes) {
    const scaling = {};
    dishes.forEach(({main}) => {
        scaling[main.uuid || main.id] = main.scaling;
    });
    return scaling;
}

/**
 * Calculate servings needed for a single participant on a specific dish
 * Handles USER vs other participants, logged servings vs portions
 *
 * @param {Object} participant - Participant object with uuid and portion
 * @param {Object} dish - Dish/meal object
 * @param {Object} dishContent - Recipe or food content object
 * @returns {number} Number of servings for this participant
 *
 * @example
 * const participant = { uuid: 'USER', portion: 1 };
 * const dish = { logged_amount: 1.5, logged_unit: 'serving' };
 * const recipe = { servings: 4 };
 * calculateParticipantServings(participant, dish, recipe); // 1.5 (uses logged amount)
 */
export function calculateParticipantServings(participant, dish, dishContent) {
    if (participant.uuid === 'USER') {
        // For primary user: try to get logged servings from the meal
        // Fall back to their portion value if no logged data available
        const loggedServings = getLoggedServingsOfRecipe(dish, dishContent);
        return loggedServings !== undefined ? loggedServings : (participant.portion || 1);
    }
    // For other participants: use their standard portion
    return participant.portion || 0;
}

/**
 * Calculate total servings needed for a set of participants on a dish
 *
 * @param {Array} participants - Array of participant objects
 * @param {Object} dish - Dish/meal object
 * @param {Object} dishContent - Recipe or food content object
 * @returns {number} Total servings needed
 *
 * @example
 * const participants = [
 *   { uuid: 'USER', portion: 1 },
 *   { uuid: 'family-1', portion: 0.75 },
 *   { uuid: 'family-2', portion: 0.5 }
 * ];
 * calculateTotalServingsForParticipants(participants, dish, recipe); // 2.25
 */
export function calculateTotalServingsForParticipants(participants, dish, dishContent) {
    const total = participants.reduce((sum, participant) => {
        return sum + calculateParticipantServings(participant, dish, dishContent);
    }, 0);
    return roundToDecimals(total, 2);
}

/**
 * Calculate required scaling (number of batches) based on servings needed
 *
 * @param {number} servingsNeeded - Total servings required
 * @param {number} baseServings - Base servings per batch (recipe yield)
 * @returns {number} Number of batches needed (minimum 1)
 *
 * @example
 * calculateRequiredScaling(7, 4); // 2 (need 2 batches of 4 to get 8 servings)
 * calculateRequiredScaling(0, 4); // 1 (minimum 1 batch)
 * calculateRequiredScaling(4, 4); // 1 (exactly 1 batch)
 */
export function calculateRequiredScaling(servingsNeeded, baseServings) {
    if (servingsNeeded <= 0 || baseServings <= 0) {
        return 1; // Minimum 1 batch
    }
    return Math.ceil(servingsNeeded / baseServings);
}

/**
 * Calculate total yield based on scaling and base servings
 *
 * @param {number} scaling - Number of batches
 * @param {number} baseServings - Servings per batch
 * @returns {number} Total yield
 *
 * @example
 * calculateTotalYield(3, 4); // 12 (3 batches of 4 servings each)
 */
export function calculateTotalYield(scaling, baseServings) {
    return scaling * baseServings;
}

/**
 * Get base servings from a dish's recipe or food content
 *
 * @param {Object} dishContent - Recipe or food content object
 * @returns {number} Base servings (default 1)
 *
 * @example
 * getBaseServings({ servings: 6 }); // 6
 * getBaseServings({}); // 1
 * getBaseServings(null); // 1
 */
export function getBaseServings(dishContent) {
    return dishContent?.servings || 1;
}

/**
 * Calculate excess servings a recipe produces beyond what participants need
 *
 * @param {Object} content - Recipe or food content object
 * @param {number} scaling - Number of batches
 * @param {Array} participants - Array of participant objects with .portion
 * @returns {number} Excess servings (yield minus needed)
 *
 * @example
 * getExcessServings({ servings: 4 }, 2, [{ portion: 1 }, { portion: 1 }]); // 6
 */
export function getExcessServings(content, scaling, participants) {
    const servings = getBaseServings(content);
    const needed = (participants || []).reduce((total, p) => total + p.portion, 0);
    return servings * (scaling || 1) - needed;
}

/**
 * Find a saved leftover meal that matches a pending leftover slot.
 * Used to preserve existing logged_amount when pending leftovers are displayed.
 *
 * @param {Array} savedLeftovers - Saved leftover meal objects from dish.leftovers
 * @param {Object} pendingLeftover - Pending leftover slot from LeftoverDatesModal
 * @returns {Object|null} Matching saved leftover, or null if this is a new slot
 */
export function findSavedLeftover(savedLeftovers, pendingLeftover) {
    if (!savedLeftovers?.length) return null;
    if (pendingLeftover.leftoverOffset !== undefined) {
        return savedLeftovers.find(s =>
            s.offset === pendingLeftover.leftoverOffset && s.meal === pendingLeftover.mealType
        ) ?? null;
    }
    if (pendingLeftover.leftoverDate) {
        const d = pendingLeftover.leftoverDate.format
            ? pendingLeftover.leftoverDate.format('YYYY-MM-DD')
            : pendingLeftover.leftoverDate;
        return savedLeftovers.find(s => s.date === d && s.meal === pendingLeftover.mealType) ?? null;
    }
    return null;
}
